
import * as React from 'react';
import roofPitchTooltip from './projectConfiguration/fieldsTooltips/roofPitchTooltip';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CustomTooltip from '__common/components/CustomTooltip';
import { InfoOutlined } from '@mui/icons-material';
import { isRMIFIProduct, isRMGridFlex, isEcoFoot2Plus } from '__common/constants/products';
import { SET_PROJECT_OPTION } from './projectConfiguration/projectConfigurationActions';
import { dispatch } from '__common/store';
import { roofPitchUpdate } from '__common/utils/versionCompare/versionCompare';

type Props = {
  initData: projectEnvConfig,
  productId: number,
  projectVersion: string;
};

export class RoofPitchSelectorStripped extends React.Component<Props, {}> {
  roofPitches = [
    '---',
    '1/12',
    '2/12',
    '3/12',
    '4/12',
    '5/12',
    '6/12',
    '7/12',
    '8/12',
    '9/12',
    '10/12',
    '11/12',
    '12/12',
  ];

  roofPitchesDegrees = [
    '---',
    ...Array.from({ length: 45 }, (_, i) => {
      const degree = i + 1;
      const pitchDegrees: { [key: number]: string } = {
        5: '1/12',
        9: '2/12',
        14: '3/12',
        18: '4/12',
        22: '5/12',
        26: '6/12',
        30: '7/12',
        33: '8/12',
        36: '9/12',
        39: '10/12',
        42: '11/12',
        45: '12/12',
      };
      const pitch = pitchDegrees[degree];
      return pitch ? `${degree}° (${pitch})` : `${degree}°`;
    })
  ];

  roofPitchesIFI = [
    '---',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
  ];

  roofPitchesIFILimitedForUNIRAC_FLASHLOC_RM = [ // if UNIRAC_FLASHLOC_RM is selected, then only these roof pitches are available for selection in the roof pitch selector dropdown list 
    '---',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
  ];

  roofPitchesIFILimited = [
    '---',
    '0',
    '1',
    '2',
    '3',
  ];

  asce716RoofPitch = [
    '---',
    '2/12',
    '3/12',
    '4/12',
    '5/12',
    '6/12',
    '7/12',
    '8/12',
    '9/12',
    '10/12',
    '11/12',
    '12/12',
  ];

  getRoofPitches = () => {
    const { productId, projectVersion } = this.props;

    if (isRMIFIProduct(productId)) {
      return this.roofPitchesIFI;
    }

    if(isEcoFoot2Plus(productId)) {
      return this.roofPitchesIFILimited;
    }

    return roofPitchUpdate(projectVersion) ? this.roofPitchesDegrees : this.roofPitches;
  }

  onRoofPitchChange = (roofPitch: string) => {
    dispatch(SET_PROJECT_OPTION('roof_pitch', roofPitch))
  }

  warningMessage = (productId) => {
    return (
      <div className="input-warning lack-of-roof-pitch">
        You should set roof {isRMGridFlex(productId) ? 'slope' : 'pitch'}
      </div>
    );
  }

  getLabel = (productId) => {

    if (isRMGridFlex(productId) || isEcoFoot2Plus(productId)) {
      return <div className="input-label">Select roof slope (degrees) </div>;
    }

    return (
      <div className="input-label">
        Select roof pitch{' '}
        <CustomTooltip title={roofPitchTooltip()} placement="bottom" isHtml>
          <InfoOutlined className="flyout-info-icon" sx={{ marginLeft: 0.5 }} />
        </CustomTooltip>
      </div>
    );
  }

  roofPitchSelect(roofPitches: string[], label) {

    const roofPitch = this.props.initData.roof_pitch;
    const productId = this.props.productId;
    return (
      <div className="array-setting-field roof-pitch-field">
        {roofPitch === '---' && this.warningMessage(productId)}
        {label}
        <Select
          id='roof_pitch_trimmed'
          value={roofPitch}
          options={roofPitches.map(pitch => ({ label: pitch, value: pitch }))}
          onChange={(value) => this.onRoofPitchChange(value)}
        />
      </div>
    );
  }

  render() {
    const { productId } = this.props;

    const roofPitches = this.getRoofPitches();

    const label = this.getLabel(productId);

    if (roofPitches.length === 0) {
      return null;
    }

    return this.roofPitchSelect(roofPitches, label);
  }
}
